<template>
	<div>
		<button
			class="filter-button"
			:class="{ 'filter-button--active': isOpen }"
			@click="handleToggle"
		>
			<AppControlIcon
				class="filter-button__icon"
				:iconName="IconTypes.FILTER"
				:size="20"
			/>
			<div class="filter-button__divider" />
			<span class="filter-button__label">{{
				translate(selectedLabel)
			}}</span>
		</button>
		<ul v-if="isOpen" class="filter-list">
			<li
				v-for="(item, index) in items"
				:key="index"
				class="filter-item"
				:class="{ 'filter-item--active': selectedItems.includes(item) }"
				@click="handleItemClick(item)"
			>
				{{ translate(item) }}
			</li>
		</ul>
	</div>
</template>

<script lang="ts" setup>
import AppControlIcon from "@/components/AppControlIcon.vue";
import SoundService from "@/game/SoundService";
import { IconTypes } from "@/types/ui";
import type { FilterItems } from "@/types/upgrades";
import { translate } from "@/utils/translate";
import { markRaw, ref } from "vue";

const soundService = markRaw(SoundService.getInstance());

const isOpen = ref<boolean>(false);
const selectedItems = ref<FilterItems[]>([]);

const emit = defineEmits(["labelSelected"]);

interface Props {
	label: FilterItems;
	items: FilterItems[];
}

const { label, items } = defineProps<Props>();

const handleToggle = (): void => {
	isOpen.value = !isOpen.value;
	soundService.playGenerickClick();
};

const selectedLabel = ref<string>(label);

const handleItemClick = (item: FilterItems): void => {
	if (selectedItems.value[0] === item) {
		selectedItems.value = [];
		selectedLabel.value = label;
	} else {
		selectedItems.value = [item];
		selectedLabel.value = item;
	}
	emit("labelSelected", selectedLabel.value);
	soundService.playGenerickClick();
	isOpen.value = false;
};
</script>

<style lang="scss" scoped>
.filter-button {
	@include flex-row-center;
	margin: 0.38rem 0.25rem 0.25rem 0.81rem;
	// padding: 0.25rem 0.88rem 0.25rem 0.44rem;
	box-shadow: inset 0 0 0.5rem 0 rgba(0, 0, 0, 0.3);
	border-width: 0.075rem;
	border-style: solid;
	border-image-source: linear-gradient(to top, #bd9032, #f0d386);
	border-image-slice: 1;
	background-image: linear-gradient(to top, #181a1d, #303239);
	width: 6.44rem;
	height: 2.06rem;

	// TODO we need to visualize the active state in Zeplin
	&--active {
		background-color: #007bff;
		color: #fff;
	}

	&__icon {
		flex-shrink: 0;
		margin: 0 0.125rem;
	}

	&__divider {
		flex-shrink: 0;
		border: solid 0 #000;
		background-color: #f2dcb5;
		width: 0.063rem;
		height: 1.563rem;
	}

	&__label {
		flex: 1 1 0;
		align-items: center;
		justify-items: left;
		padding-right: 0.25rem;
		padding-left: 0.5rem;
		min-width: 0;
		color: #f2dcb5;
		font-weight: 800;
		// font-size: 1rem;
		font-size: 0.875rem;
		@include text-truncate;
		// FRV
		margin-top: -0.25rem;
	}
}

.filter-list {
	position: absolute;
	z-index: 9;
	margin: 0.5rem 0 0;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	border-radius: 4px;
	background-color: #f8f9fa;
	padding: 0.5rem 0;
	width: 10rem;
	list-style: none;

	.filter-item {
		transition: background-color 0.2s ease;
		cursor: pointer;
		padding: 0.25rem 0.75rem;
		font-size: 0.875rem;

		&--active {
			background-color: #007bff;
			color: #fff;
		}
	}
}
</style>
